import React, { useState, useEffect, useMemo } from 'react';
import { auth } from '../../../../../../firebase/config';
import Modal from '../../../../../../components/Modal';
import TextField from '../../../../../../components/TextField';
import Button from '../../../../../../components/Button';
import { FiSearch, FiAlertTriangle } from 'react-icons/fi';
import Icon from '../../../../../../components/Icon';
import {
    updateDeploymentRequestVendors,
    updateDeploymentRequestStatus,
    fetchDeploymentRequestBankId,
    calculateRegionalVendorShare,
    deployCompaniesFromRequest
} from '../../../service/deploymentRequestService';
import { fetchCompaniesData } from '../../../../../companies/service/companiesService';
import type { DeploymentRequest } from '../../../service/deploymentRequestService';
import type { Company } from '../../../../../companies/service/companiesService';
import Toast from '../../../../../../components/Toast';
import { fetchDeployedCompaniesForBranch } from '../../../../../deployments/components/AddDeploymentModal/service/addDeploymentService';
import { getDocs, collection } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';

type EditDeploymentRequestModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    request: DeploymentRequest | null;
};

type ShareStats = {
    allocatedCount: number;
    totalBranches: number;
    percentage: number;
} | null;

export default function EditDeploymentRequestModal({ isOpen, onClose, onSuccess, request }: EditDeploymentRequestModalProps) {
    const [selectedCctv, setSelectedCctv] = useState<Company | null>(null);
    const [selectedBams, setSelectedBams] = useState<Company | null>(null);
    const [selectedGuard, setSelectedGuard] = useState<Company | null>(null);
    
    // Share percentages
    const [cctvShare, setCctvShare] = useState<ShareStats>(null);
    const [bamsShare, setBamsShare] = useState<ShareStats>(null);
    const [guardShare, setGuardShare] = useState<ShareStats>(null);
    const [loadingShares, setLoadingShares] = useState({ cctv: false, bams: false, guard: false });

    // Region precalculation state (for company modal)
    const [regionTotalBranches, setRegionTotalBranches] = useState(0);
    const [regionCompanyShareMap, setRegionCompanyShareMap] = useState<{ [companyId: string]: number }>({});
    const [loadingRegionStats, setLoadingRegionStats] = useState(false);
    
    // Fields
    const [requestDate, setRequestDate] = useState('');
    const [coveredArea, setCoveredArea] = useState('');
    const [renovationStartDate, setRenovationStartDate] = useState('');
    const [renovationCompletionDate, setRenovationCompletionDate] = useState('');
    const [reason, setReason] = useState('');
    const [cctvKpiRemarks, setCctvKpiRemarks] = useState('');
    const [bamsKpiRemarks, setBamsKpiRemarks] = useState('');
    const [guardKpiRemarks, setGuardKpiRemarks] = useState('');

    const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
    const [deployedEntries, setDeployedEntries] = useState<any[]>([]);
    const [loadingDeployed, setLoadingDeployed] = useState(false);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    
    // Selection Submodal
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [selectedCompanyType, setSelectedCompanyType] = useState<'CCTV' | 'Guard Company' | 'BAMS' | null>(null);
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    
    // UI states
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [saving, setSaving] = useState(false);

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const isRegionalHead = sessionStorage.getItem('psoDesignation')?.toLowerCase().includes('regional') || false;

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    // Load available companies
    useEffect(() => {
        if (isOpen && uid) {
            const loadData = async () => {
                setLoadingCompanies(true);
                try {
                    const companies = await fetchCompaniesData(uid, isBankUser);
                    setAvailableCompanies(companies);
                } catch (err) {
                    console.error('Error fetching companies:', err);
                    showToast('Failed to load companies', 'error');
                } finally {
                    setLoadingCompanies(false);
                }
            };
            loadData();
        }
    }, [isOpen, uid, isBankUser]);

    // Precalculate regional shares
    const loadRegionStats = async (branchId: string, locId: string) => {
        if (!uid || !locId) return;
        setLoadingRegionStats(true);
        try {
            const bankId = await fetchDeploymentRequestBankId(uid, isBankUser);
            if (!bankId) return;

            // 1. Get regional branches
            const branchesSnap = await getDocs(collection(db, "banks", bankId, "branch"));
            const regionBranches = branchesSnap.docs
                .map(d => ({ id: d.id, ...d.data() } as any))
                .filter(b => b.locationId === locId);

            // 2. Get active deployments
            const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
            const activeDeployments = deploymentsSnap.docs.map(doc => doc.data() as any);

            // 3. Map company counts
            const counts: { [companyId: string]: number } = {};
            activeDeployments.forEach(dep => {
                if (dep.companiesId && regionBranches.some(rb => rb.id === dep.branchId)) {
                    counts[dep.companiesId] = (counts[dep.companiesId] || 0) + 1;
                }
            });

            setRegionTotalBranches(regionBranches.length);
            setRegionCompanyShareMap(counts);
        } catch (error) {
            console.error("Error loading regional share stats:", error);
        } finally {
            setLoadingRegionStats(false);
        }
    };

    // Populate data when request changes
    useEffect(() => {
        if (isOpen && request) {
            setSelectedCctv(request.cctvCompany || null);
            setSelectedBams(request.bamsCompany || null);
            setSelectedGuard(request.guardCompany || null);
            
            setRequestDate(request.requestDate || '');
            setCoveredArea(request.coveredArea?.toString() || '');
            setRenovationStartDate(request.renovationStartDate || '');
            setRenovationCompletionDate(request.renovationCompletionDate || '');
            setReason(request.reason || '');
            setCctvKpiRemarks(request.cctvKpiRemarks || '');
            setBamsKpiRemarks(request.bamsKpiRemarks || '');
            setGuardKpiRemarks(request.guardKpiRemarks || '');

            if (request.branch?.id && request.branch?.locationId) {
                loadRegionStats(request.branch.id, request.branch.locationId);
            }

            // Load currently deployed entries
            if (uid && request.branchId) {
                const loadDeployed = async () => {
                    setLoadingDeployed(true);
                    try {
                        const deployed = await fetchDeployedCompaniesForBranch(uid, isBankUser, request.branchId);
                        setDeployedEntries(deployed);
                    } catch (err) {
                        console.error('Error loading deployed companies:', err);
                    } finally {
                        setLoadingDeployed(false);
                    }
                };
                loadDeployed();
            }
        }
    }, [isOpen, request, uid, isBankUser]);

    // Reset when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedCctv(null);
            setSelectedBams(null);
            setSelectedGuard(null);
            setCctvShare(null);
            setBamsShare(null);
            setGuardShare(null);
            setRegionTotalBranches(0);
            setRegionCompanyShareMap({});
            setLoadingRegionStats(false);
            setLoadingCompanies(false);
            setDeployedEntries([]);
            setCompanySearchTerm('');
            setShowCompanyModal(false);
            setSaving(false);
            setReason('');
            setCctvKpiRemarks('');
            setBamsKpiRemarks('');
            setGuardKpiRemarks('');
        }
    }, [isOpen]);

    // Calculate shares for form display
    const fetchShare = async (companyId: string, type: 'cctv' | 'bams' | 'guard') => {
        if (!uid || !request?.branch?.locationId) return;
        setLoadingShares(prev => ({ ...prev, [type]: true }));
        try {
            const bankId = await fetchDeploymentRequestBankId(uid, isBankUser);
            if (bankId) {
                const stats = await calculateRegionalVendorShare(bankId, request.branch.locationId, companyId);
                if (type === 'cctv') setCctvShare(stats);
                else if (type === 'bams') setBamsShare(stats);
                else if (type === 'guard') setGuardShare(stats);
            }
        } catch (error) {
            console.error(`Error loading stats for ${type}:`, error);
        } finally {
            setLoadingShares(prev => ({ ...prev, [type]: false }));
        }
    };

    useEffect(() => {
        if (selectedCctv && request?.branch) {
            fetchShare(selectedCctv.id, 'cctv');
        } else {
            setCctvShare(null);
        }
    }, [selectedCctv, request]);

    useEffect(() => {
        if (selectedBams && request?.branch) {
            fetchShare(selectedBams.id, 'bams');
        } else {
            setBamsShare(null);
        }
    }, [selectedBams, request]);

    useEffect(() => {
        if (selectedGuard && request?.branch) {
            fetchShare(selectedGuard.id, 'guard');
        } else {
            setGuardShare(null);
        }
    }, [selectedGuard, request]);

    const filteredCompanies = useMemo(() => {
        let list = availableCompanies;
        if (selectedCompanyType === 'CCTV') {
            list = list.filter(c => c.companyType === 'CCTV');
        } else if (selectedCompanyType === 'Guard Company') {
            list = list.filter(c => c.companyType === 'Guard Company' || c.companyType?.toLowerCase().includes('guard'));
        } else if (selectedCompanyType === 'BAMS') {
            list = list.filter(c => c.companyType === 'BAMS');
        }

        const term = companySearchTerm.toLowerCase();
        return list.filter(c => (c.companyName || '').toLowerCase().includes(term));
    }, [availableCompanies, selectedCompanyType, companySearchTerm]);

    const handleSelectCompany = (company: Company) => {
        if (selectedCompanyType === 'CCTV') {
            setSelectedCctv(company);
        } else if (selectedCompanyType === 'Guard Company') {
            setSelectedGuard(company);
        } else if (selectedCompanyType === 'BAMS') {
            setSelectedBams(company);
        }
        setShowCompanyModal(false);
        setCompanySearchTerm('');
        setSelectedCompanyType(null);
    };

    const handleSaveChanges = async () => {
        if (!request || !uid) return;

        if (!requestDate) {
            showToast('Please enter request date', 'error');
            return;
        }

        if (!coveredArea.trim()) {
            showToast('Please enter covered area', 'error');
            return;
        }

        if (!renovationStartDate || !renovationCompletionDate) {
            showToast('Please enter renovation start and completion dates', 'error');
            return;
        }

        if (new Date(renovationStartDate) > new Date(renovationCompletionDate)) {
            showToast('Renovation completion date cannot be before start date', 'error');
            return;
        }

        if (!selectedCctv && !selectedBams && !selectedGuard) {
            showToast('Please select at least one vendor', 'warning');
            return;
        }

        setSaving(true);
        try {
            const bankId = await fetchDeploymentRequestBankId(uid, isBankUser);
            if (!bankId) {
                showToast('Bank ID not found', 'error');
                setSaving(false);
                return;
            }

            // 1. Update vendors and fields
            await updateDeploymentRequestVendors(
                bankId,
                request.id,
                requestDate,
                coveredArea.trim(),
                renovationStartDate,
                renovationCompletionDate,
                selectedCctv?.id || null,
                selectedBams?.id || null,
                selectedGuard?.id || null,
                reason.trim(),
                cctvKpiRemarks.trim(),
                bamsKpiRemarks.trim(),
                guardKpiRemarks.trim()
            );

            // If bank user edits an already approved request, trigger deployment updates
            if (isBankUser && request.status === 'Approved') {
                await deployCompaniesFromRequest(
                    bankId,
                    request.branchId,
                    selectedCctv?.id || null,
                    selectedBams?.id || null,
                    selectedGuard?.id || null,
                    request.psoId || uid
                );
            }

            // 2. Trigger resubmission if status was Rejected or Cancelled (only for non-bank users)
            if (!isBankUser && (request.status === 'Rejected' || request.status === 'Cancelled')) {
                if (isRegionalHead) {
                    const forwardedByName = sessionStorage.getItem('name') || auth.currentUser?.displayName || 'Regional PSO';
                    await updateDeploymentRequestStatus(bankId, request.id, 'Forwarded to Head', {
                        forwardedBy: uid,
                        forwardedByName,
                        regionalHeadRemarks: 'Resubmitted after correction.'
                    });
                    showToast('✅ Resubmitted and Forwarded to Head!', 'success');
                } else {
                    await updateDeploymentRequestStatus(bankId, request.id, 'Pending');
                    showToast('✅ Resubmitted to Regional Head successfully!', 'success');
                }
            } else {
                showToast('✅ Allocation request updated successfully!', 'success');
            }

            setTimeout(() => {
                onSuccess();
                onClose();
            }, 1500);
        } catch (error) {
            console.error('Error updating allocation request:', error);
            showToast('Failed to save changes', 'error');
            setSaving(false);
        }
    };

    const renderShareStats = (stats: ShareStats, typeLabel: string) => {
        if (!stats || !request?.branch) return null;
        const isHigh = stats.percentage > 50;
        const colorClass = isHigh 
            ? 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/20 dark:text-amber-300 dark:border-amber-900/30'
            : 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/20 dark:text-blue-300 dark:border-blue-900/30';
            
        return (
            <div className={`mt-2 flex items-center gap-2 rounded-lg border p-2 text-xs font-semibold ${colorClass}`}>
                {isHigh && <Icon icon={FiAlertTriangle} className="h-4 w-4 shrink-0 text-amber-500" />}
                <span>
                    {typeLabel} share in <strong>{request.branch.locationName}</strong>: {stats.allocatedCount} / {stats.totalBranches} branches ({stats.percentage}%)
                </span>
            </div>
        );
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title={`Edit Allocation Request (${request?.requestId})`} size="lg">
                <div className="space-y-6 px-6 max-h-[75vh] overflow-y-auto">
                    
                    {/* Branch (Immutable) */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Branch (Immutable)
                        </label>
                        <TextField
                            type="text"
                            value={request?.branch ? `${request.branch.branchCode} - ${request.branch.address}` : ""}
                            readOnly
                            disabled
                            className="bg-gray-100 dark:bg-gray-800"
                        />
                    </div>

                    {/* Current Deployments Display */}
                    {request?.branch && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-800/30">
                            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                Currently Deployed Vendors
                            </h4>
                            {loadingDeployed ? (
                                <p className="text-xs text-gray-500 italic">Loading deployed vendors...</p>
                            ) : deployedEntries.length === 0 ? (
                                <p className="text-xs text-gray-500 italic">No vendors currently deployed.</p>
                            ) : (
                                <div className="space-y-1">
                                    {deployedEntries.map((e, index) => (
                                        <div key={index} className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                                            <span>{e.company.companyName}</span>
                                            <span className="font-semibold text-blue-600 dark:text-blue-400">{e.company.companyType}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Timeline & Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Request Date *
                            </label>
                            <input
                                type="date"
                                value={requestDate}
                                onChange={(e) => setRequestDate(e.target.value)}
                                className="w-full rounded-xl border border-gray-300 bg-white p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Covered Area (sq ft) *
                            </label>
                            <TextField
                                type="number"
                                placeholder="e.g. 2400"
                                value={coveredArea}
                                onChange={(e) => setCoveredArea(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Renovation Start Date *
                            </label>
                            <input
                                type="date"
                                value={renovationStartDate}
                                onChange={(e) => setRenovationStartDate(e.target.value)}
                                className="w-full rounded-xl border border-gray-300 bg-white p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Renovation Completion Date *
                            </label>
                            <input
                                type="date"
                                value={renovationCompletionDate}
                                onChange={(e) => setRenovationCompletionDate(e.target.value)}
                                className="w-full rounded-xl border border-gray-300 bg-white p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            />
                        </div>
                    </div>

                    {/* Vendor Selection fields */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 border-b pb-1">
                            Modify Selected Vendors:
                        </h3>

                        {/* CCTV Company */}
                        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                CCTV Vendor
                            </label>
                            <div className="flex gap-2 space-x-2">
                                <TextField
                                    type="text"
                                    placeholder="Select CCTV Vendor"
                                    value={selectedCctv ? selectedCctv.companyName : ""}
                                    readOnly
                                    className="bg-gray-50 dark:bg-gray-800 flex-1"
                                />
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setSelectedCompanyType('CCTV');
                                        setShowCompanyModal(true);
                                    }}
                                >
                                    Select
                                </Button>
                                {selectedCctv && (
                                    <Button variant="secondary" onClick={() => setSelectedCctv(null)} className="text-red-500 border-red-200 hover:bg-red-50">
                                        Clear
                                    </Button>
                                )}
                            </div>
                            {selectedCctv && (
                                <div className="mt-3">
                                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                                        CCTV KPI Remarks (Optional)
                                    </label>
                                    <textarea
                                        value={cctvKpiRemarks}
                                        onChange={(e) => setCctvKpiRemarks(e.target.value)}
                                        placeholder="Enter CCTV specific KPI remarks..."
                                        rows={2}
                                        className="w-full rounded-xl border border-gray-300 bg-white p-2.5 text-xs outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                    />
                                </div>
                            )}
                            {renderShareStats(cctvShare, selectedCctv?.companyName || '')}
                        </div>

                        {/* BAMS Company */}
                        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                BAMS Vendor
                            </label>
                            <div className="flex gap-2 space-x-2">
                                <TextField
                                    type="text"
                                    placeholder="Select BAMS Vendor"
                                    value={selectedBams ? selectedBams.companyName : ""}
                                    readOnly
                                    className="bg-gray-50 dark:bg-gray-800 flex-1"
                                />
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setSelectedCompanyType('BAMS');
                                        setShowCompanyModal(true);
                                    }}
                                >
                                    Select
                                </Button>
                                {selectedBams && (
                                    <Button variant="secondary" onClick={() => setSelectedBams(null)} className="text-red-500 border-red-200 hover:bg-red-50">
                                        Clear
                                    </Button>
                                )}
                            </div>
                            {selectedBams && (
                                <div className="mt-3">
                                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                                        BAMS KPI Remarks (Optional)
                                    </label>
                                    <textarea
                                        value={bamsKpiRemarks}
                                        onChange={(e) => setBamsKpiRemarks(e.target.value)}
                                        placeholder="Enter BAMS specific KPI remarks..."
                                        rows={2}
                                        className="w-full rounded-xl border border-gray-300 bg-white p-2.5 text-xs outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                    />
                                </div>
                            )}
                            {renderShareStats(bamsShare, selectedBams?.companyName || '')}
                        </div>

                        {/* Guard Company */}
                        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Guarding Vendor
                            </label>
                            <div className="flex gap-2 space-x-2">
                                <TextField
                                    type="text"
                                    placeholder="Select Guard Vendor"
                                    value={selectedGuard ? selectedGuard.companyName : ""}
                                    readOnly
                                    className="bg-gray-50 dark:bg-gray-800 flex-1"
                                />
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setSelectedCompanyType('Guard Company');
                                        setShowCompanyModal(true);
                                    }}
                                >
                                    Select
                                </Button>
                                {selectedGuard && (
                                    <Button variant="secondary" onClick={() => setSelectedGuard(null)} className="text-red-500 border-red-200 hover:bg-red-50">
                                        Clear
                                    </Button>
                                )}
                            </div>
                            {selectedGuard && (
                                <div className="mt-3">
                                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                                        Guarding KPI Remarks (Optional)
                                    </label>
                                    <textarea
                                        value={guardKpiRemarks}
                                        onChange={(e) => setGuardKpiRemarks(e.target.value)}
                                        placeholder="Enter guarding specific KPI remarks..."
                                        rows={2}
                                        className="w-full rounded-xl border border-gray-300 bg-white p-2.5 text-xs outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                    />
                                </div>
                            )}
                            {renderShareStats(guardShare, selectedGuard?.companyName || '')}
                        </div>
                    </div>

                    {/* Remarks */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Remarks
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Remarks..."
                            rows={4}
                            className="w-full rounded-xl border border-gray-300 bg-white p-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="secondary" onClick={onClose} disabled={saving}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveChanges} disabled={saving}>
                            {saving ? 'Saving...' : (request?.status === 'Rejected' || request?.status === 'Cancelled' ? 'Edit & Resubmit' : 'Save Changes')}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Company Selection Modal */}
            <Modal
                isOpen={showCompanyModal}
                onClose={() => {
                    setShowCompanyModal(false);
                    setSelectedCompanyType(null);
                }}
                title={`Select ${selectedCompanyType || 'Company'}`}
                size="xl"
            >
                <div className="space-y-4 px-6">


                    <div className="relative">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search company name..."
                            value={companySearchTerm}
                            onChange={(e) => setCompanySearchTerm(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30"
                        />
                    </div>
                    
                    <div className="max-h-72 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b bg-gray-50 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                                    <th className="px-4 py-2.5">Company Name</th>
                                    <th className="px-4 py-2.5">Type</th>
                                    {request?.branch && <th className="px-4 py-2.5">Regional Share ({request.branch.locationName})</th>}
                                    <th className="px-4 py-2.5">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingCompanies || loadingRegionStats ? (
                                    <tr>
                                        <td colSpan={request?.branch ? 4 : 3} className="px-4 py-6 text-center text-sm text-gray-500">Loading vendors and calculating shares...</td>
                                    </tr>
                                ) : filteredCompanies.length === 0 ? (
                                    <tr>
                                        <td colSpan={request?.branch ? 4 : 3} className="px-4 py-6 text-center text-sm text-gray-500">No companies found</td>
                                    </tr>
                                ) : (
                                    filteredCompanies.map(company => {
                                        const allocatedCount = regionCompanyShareMap[company.id] || 0;
                                        const pct = regionTotalBranches > 0 ? Math.round((allocatedCount / regionTotalBranches) * 100) : 0;
                                        
                                        return (
                                            <tr key={company.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800/40">
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{company.companyName}</td>
                                                <td className="px-4 py-3">
                                                    <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                                                        {company.companyType}
                                                    </span>
                                                </td>
                                                {request?.branch && (
                                                    <td className="px-4 py-3 text-sm font-semibold">
                                                        <span className={pct > 50 ? "text-amber-600 font-bold" : "text-blue-600"}>
                                                            {allocatedCount} / {regionTotalBranches} branches ({pct}%)
                                                        </span>
                                                    </td>
                                                )}
                                                <td className="px-4 py-3">
                                                    <Button onClick={() => handleSelectCompany(company)} className="px-2.5 py-1 text-xs">Select</Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>

            {/* Toast alerts */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
